import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Car, ShoppingCart, Wrench, BadgeDollarSign, Receipt,
  Building2, FileBarChart2, Users, LogOut, Menu, X, BookOpen, Tags, Undo2, Redo2, History,
} from "lucide-react";
import { ROLES, can } from "@/lib/labels";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "lihat_laporan", testid: "nav-dashboard" },
  { to: "/mobil", label: "Stok Mobil", icon: Car, testid: "nav-cars" },
  { to: "/pricelist", label: "Pricelist", icon: Tags, testid: "nav-pricelist" },
  { to: "/pembelian", label: "Pembelian", icon: ShoppingCart, perm: ["lihat_hpp", "kelola_pembelian"], testid: "nav-purchases" },
  { to: "/biaya", label: "Biaya Mobil", icon: Wrench, testid: "nav-costs" },
  { to: "/penjualan", label: "Penjualan", icon: BadgeDollarSign, testid: "nav-sales" },
  { to: "/operasional", label: "Biaya Operasional", icon: Receipt, perm: "input_biaya_operasional", testid: "nav-operational" },
  { to: "/buku-kas", label: "Buku Kas", icon: BookOpen, testid: "nav-cashbook" },
  { to: "/cabang", label: "Cabang", icon: Building2, testid: "nav-branches" },
  { to: "/laporan", label: "Laporan", icon: FileBarChart2, perm: "lihat_laporan", testid: "nav-reports" },
  { to: "/pengguna", label: "Pengguna", icon: Users, admin: true, testid: "nav-users" },
  { to: "/riwayat", label: "Riwayat Perubahan", icon: History, admin: true, testid: "nav-audit" },
];

function SidebarContent({ user, onLogout, onNavigate }) {
  const navItems = useMemo(() => NAV.filter((n) => (!n.admin || user?.role === "admin") && (!n.perm || (Array.isArray(n.perm) ? n.perm : [n.perm]).some((p) => can(user, p)))), [user]);
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-[#1A1A22]">
        <img src="/logo.jpg" alt="ForzaMobil.id" className="w-10 h-10 rounded-full object-cover ring-1 ring-white/10" />
        <div>
          <p className="font-heading font-800 font-extrabold text-zinc-50 leading-none">ForzaMobil.id</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">Dealer ERP</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-blue-600/10 text-blue-400 border border-blue-500/25 shadow-[0_0_15px_rgba(37,99,235,0.15)]"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-[#121218]/60 border border-transparent"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#1A1A22]">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-[#121218] border border-[#26262E] flex items-center justify-center text-xs font-bold text-blue-500">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-100 truncate">{user?.name}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">{ROLES[user?.role] || user?.role}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="p-2 text-zinc-500 hover:text-rose-500 transition-colors duration-200"
            title="Keluar"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function UndoRedo() {
  const [st, setSt] = useState({ undo: [], redo: [] });
  const location = useLocation();
  const load = () => api.get("/undo/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, [location.pathname]);

  const doUndo = async () => {
    try {
      const r = await api.post("/undo");
      toast.success(`Dibatalkan: ${r.data.label}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doRedo = async () => {
    try {
      const r = await api.post("/redo");
      toast.success(`Dikembalikan: ${r.data.label}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        data-testid="undo-button"
        disabled={!st.undo.length}
        onClick={doUndo}
        title={st.undo[0] ? `Undo: ${st.undo[0].label}` : "Tidak ada pencatatan yang bisa di-undo"}
        className="p-2 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-[#121218] disabled:opacity-30 disabled:pointer-events-none transition-colors duration-200"
      >
        <Undo2 className="w-4 h-4" />
      </button>
      <button
        data-testid="redo-button"
        disabled={!st.redo.length}
        onClick={doRedo}
        title={st.redo[0] ? `Redo: ${st.redo[0].label}` : "Tidak ada pencatatan yang bisa di-redo"}
        className="p-2 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-[#121218] disabled:opacity-30 disabled:pointer-events-none transition-colors duration-200"
      >
        <Redo2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <aside className="hidden lg:block fixed inset-y-0 left-0 print-hide w-64 bg-[#030303] border-r border-[#1A1A22] z-40">
        <SidebarContent user={user} onLogout={handleLogout} />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 print-hide w-64 bg-[#030303] border-r border-[#1A1A22]">
            <button
              data-testid="close-sidebar-button"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-100"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="print-hide sticky top-0 z-30 h-16 bg-[#050505]/80 backdrop-blur-2xl border-b border-[#1A1A22] flex items-center px-4 md:px-8 gap-4">
          <button
            data-testid="open-sidebar-button"
            className="lg:hidden p-2 text-zinc-400 hover:text-zinc-100"
            onClick={() => setOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <UndoRedo />
          <p className="text-xs text-zinc-500 hidden sm:block">
            {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </header>
        <main className="p-4 md:p-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
